// Which pitch-deck field is spoken by whom at the pitch. The presentation view (FR-P11, then
// WO-7/22 Track B) splits the deck into three OWNERSHIP regions — student / AE / do-not-present —
// and which region a field lands in has been moved by three separate work orders and one design
// import. Membership lives here rather than inline in PreprintDetails so a re-bucketing is one
// edit with a test behind it, instead of an unguarded array shuffle.
//
// Canonical source: design repo docs/designs/2026-07-31-preprint-pitch-deck/.

export type PitchRegion = "student" | "ae" | "context";

export const REVIEWER_PROFILE_FIELDS = [
  { key: "Reviewer Profile(discipline)", label: "What training / education should a reviewer have? (e.g. epidemiology)" },
  {
    key: "Reviewer Profile(method expertise)",
    label: "Please describe the minimum reviewer profile (describe the type of research they conduct)",
  },
  {
    key: "Reviewer Profile(topical expertise)",
    label: "Please list additional desired (but not required) reviewer characteristics (methods and/or research topics)",
  },
] as const;

/** Research question + main claim per slot, with that claim's rating parked in Context beside it. */
export const CLAIM_PAIRS = [1, 2, 3].map((n) => ({
  rq: { key: `Research Q${n}`, label: `Research Question ${n}` },
  claim: { key: `Main Claim ${n}`, label: `Main Claim ${n}` },
  rating: `Claim ${n} Rating`,
  confidence: `Claim ${n} Rating Confidence`,
  ratingLabel: `Claim ${n} Rating`,
}));

/** Student region, after the claims. */
export const PRESENTED_TAIL: { key: string; label: string }[] = [
  { key: "Potential Limitation", label: "Potential Limitations" },
  // Reason(value-add) ("beyond existing literature") removed — superseded by Priority(novelty).
];

/**
 * AE region (editorial meetings only in presentation view). All three reviewer-profile prompts
 * sit here as of the Jul-31 pitch-deck design. Before To Pitch(Editorial), presentation folds
 * these into the student column so the student pitches everything at the team meeting.
 *
 * ⚠ This REVERSES WO-7/23 Track C [10:25–11:23], which moved the minimum-reviewer-profile prompt
 * (`Reviewer Profile(method expertise)`) into the STUDENT region because "the student presents it".
 * The Jul-31 design draws it in the AE box. That design carries no grounding sync log, so the
 * reversal is flagged, not settled — see docs/qa/2026-07-31-pitch-deck-rework.md.
 */
export const PRESENTED_PROFILE_FIELDS = REVIEWER_PROFILE_FIELDS;

/**
 * Context rail — reference material, never read aloud. Order matters: it is the reading order of
 * the rail. Methods sit below abstract / evidence (WO-7/30 Track C); Angel (Aug 2026) placed the
 * key reviewer question + similar articles after Method 3 on this not-presented side.
 */
export const CONTEXT_TEXT: { key: string; label: string; stars?: boolean }[] = [
  { key: "Abstract", label: "Abstract" },
  { key: "Strength of Evidence", label: "Strength of Evidence", stars: true },
  // WO-7/30 Track C: methods sit below abstract / evidence ("not saying the methods here").
  { key: "Method Used 1", label: "Methods used" },
  { key: "Method Used 2", label: "Methods used (2)" },
  { key: "Method Used 3", label: "Methods used (3)" },
  // Angel (Aug 2026): sit with Method 3 on the not-presented rail — SoftR parity, student-facing.
  {
    key: "Key Reviewer Question",
    label:
      "What is the most important specific question that the reviewer needs to answer about this preprint?",
  },
  { key: "Nearest Neighbor Papers", label: "Similar Articles" },
];

/** The region a deck field is presented in, or null when the field has no presentation slot. */
export function regionOf(key: string): PitchRegion | null {
  for (const c of CLAIM_PAIRS) {
    if (key === c.rq.key || key === c.claim.key) return "student";
    if (key === c.rating || key === c.confidence) return "context";
  }
  if (PRESENTED_TAIL.some((f) => f.key === key)) return "student";
  if (PRESENTED_PROFILE_FIELDS.some((f) => f.key === key)) return "ae";
  if (CONTEXT_TEXT.some((f) => f.key === key)) return "context";
  return null;
}
